// PWA helpers - detect installed (standalone) mode.
// `akountfly_pwa_preview=1` in localStorage forces the installed experience (QA/testing).
// The native React Native / WebView wrapper is treated as "installed" too so
// every PWA-only affordance (biometric, background lock, sidebar chrome)
// lights up automatically when the mobile team ships an app update.
import { isNativeApp } from './nativeBridge';

export const isStandalone = () => {
  try {
    if (localStorage.getItem('akountfly_pwa_preview') === '1') return true;
  } catch { /* no-op */ }
  if (isNativeApp()) return true;
  return (
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true)
  );
};

export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/**
 * React hook that returns true when the app is running as an installed PWA
 * (or the QA `akountfly_pwa_preview=1` override is set). Live-updates when
 * the display mode changes so a mid-session install flips behaviour.
 *
 * Used to gate PWA-only UX: the fixed global "<" back button, the mobile
 * top-bar layout, the background auto-lock, and the biometric enroll nag.
 * Web browser tabs (desktop or mobile) will never see these.
 */
import { useEffect, useState } from 'react';
export function useIsStandalone() {
  const [pwa, setPwa] = useState(isStandalone());
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(display-mode: standalone)');
    const onChange = () => setPwa(isStandalone());
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return pwa;
}
